import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Zap } from "lucide-react";
import { toast } from "sonner";
import { wcApi } from "../lib/api";
import { useCart } from "../context/CartContext";
import { beep } from "../lib/sound";
import { scrollToTop } from "../lib/lenis";
import Navbar from "../components/Navbar";
import CartDrawer from "../components/CartDrawer";

const SPECS = [
  ["DISPLAY", '0.96" OLED · 128×64'],
  ["SHELL", "GRADE-5 TITANIUM"],
  ["FINISH", "RAW · NO COLORING"],
  ["WEIGHT", "14.2 G"],
  ["BATTERY", "72 H LOOP"],
  ["MADE", "BY HAND · SEOUL"],
];

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [missing, setMissing] = useState(false);
  const [size, setSize] = useState(null);

  useEffect(() => {
    scrollToTop();
    wcApi
      .get(`/products/${id}`)
      .then((r) => {
        const wpProd = r.data;
        const mappedProduct = {
          id: wpProd.id.toString(),
          name: wpProd.name,
          category: wpProd.categories?.[0]?.name || "Uncategorized",
          price: (wpProd.prices.price / (10 ** wpProd.prices.currency_minor_unit)).toFixed(2),
          anomaly_class: wpProd.attributes?.find(a => a.name === "anomaly_class")?.terms?.[0]?.name || "CLASS-C",
          signature: wpProd.attributes?.find(a => a.name === "signature")?.terms?.[0]?.name === "True",
          image: wpProd.images?.[0]?.src || "/products/placeholder.png",
          video_loop: null,
          description: wpProd.short_description ? wpProd.short_description.replace(/<[^>]+>/g, '') : "",
          story: wpProd.description ? wpProd.description.replace(/<[^>]+>/g, '') : "",
          materials: wpProd.attributes?.find(a => a.name === "materials")?.terms?.map(t => t.name) || [],
          sizes: wpProd.attributes?.find(a => a.name === "sizes")?.terms?.map(t => t.name) || null,
          fit: wpProd.attributes?.find(a => a.name === "fit")?.terms?.[0]?.name || "Standard fit",
        };
        setProduct(mappedProduct);
      })
      .catch(() => setMissing(true));
  }, [id]);

  if (missing) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black font-mono text-white/70">
        <div className="text-center">
          <p className="tracking-[0.4em]">ARTIFACT NOT FOUND</p>
          <button data-testid="product-back-button" onClick={() => navigate("/", { state: { to: "#platform" } })} className="btn-y2k mt-6 px-4 py-2 text-[10px]">
            BACK TO THE METRO
          </button>
        </div>
      </div>
    );
  }
  if (!product) return <div className="min-h-screen bg-black" />;

  const acquire = () => {
    addItem(product);
    beep(1320, 0.08, 0.05);
    toast.success("ARTIFACT SECURED", { description: size ? `${product.name} · US ${size}` : product.name });
  };

  return (
    <div className="min-h-screen bg-black font-mono text-white" data-testid="product-page">
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 pb-24 pt-24 sm:px-10">
        <button
          data-testid="product-back-button"
          onClick={() => {
            beep(520, 0.05);
            navigate("/", { state: { to: "#platform" } });
          }}
          className="btn-y2k flex items-center gap-2 px-3 py-1.5 text-[10px]"
        >
          <ArrowLeft size={12} /> BACK / 戻る
        </button>

        <div className="mt-10 grid items-start gap-14 lg:grid-cols-[1.1fr_0.9fr]">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <img
              data-testid="product-image"
              src={product.image}
              alt={product.name}
              className="anim-float mx-auto w-full max-w-2xl object-contain"
            />
            <div className="pointer-events-none absolute inset-x-[20%] bottom-6 h-10 rounded-full bg-[radial-gradient(ellipse,rgba(190,235,255,0.35),transparent_70%)] blur-lg" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="text-[10px] tracking-[0.4em] text-white/50">
              {product.category.toUpperCase()} // {product.anomaly_class}
            </p>
            <h1 data-testid="product-name" className="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
              {product.name}
            </h1>
            <p data-testid="product-price" className="mt-4 text-2xl font-bold">
              ${product.price}
            </p>
            <p className="mt-6 max-w-lg text-sm leading-relaxed text-white/80">{product.description}</p>
            <p data-testid="product-story" className="mt-4 max-w-lg text-sm leading-relaxed text-white/60">
              {product.story ||
                "Machined from a single billet, the clip carries a micro OLED sealed behind sapphire glass. The feed loops anomaly footage from the Gumz line; hold the shell for two seconds to switch channels. No plating, no coloring — the titanium is left exactly as it comes off the mill and is finished by hand in Seoul."}
            </p>

            {product.materials ? (
              <div data-testid="product-materials" className="mt-8 border border-white/15">
                <p className="border-b border-white/10 px-3 py-2 text-[9px] tracking-[0.35em] text-white/45">MATERIAL NOTES</p>
                <ul className="divide-y divide-white/10">
                  {product.materials.map((m) => (
                    <li key={m} className="px-3 py-2 text-[11px] text-white/85">— {m}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <dl className="mt-8 grid grid-cols-2 gap-px border border-white/15 bg-white/15 sm:grid-cols-3">
                {SPECS.map(([k, v]) => (
                  <div key={k} className="bg-black p-3">
                    <dt className="text-[9px] tracking-[0.3em] text-white/45">{k}</dt>
                    <dd className="mt-1 text-[11px] text-white">{v}</dd>
                  </div>
                ))}
              </dl>
            )}

            {product.sizes && (
              <div data-testid="product-size-guide" className="mt-6">
                <p className="text-[9px] tracking-[0.35em] text-white/45">SIZE GUIDE // {product.fit}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {product.sizes.map((s) => (
                    <button
                      key={s}
                      data-testid={`size-${s.replace(/\s+/g, "-")}`}
                      onClick={() => {
                        beep(760, 0.05);
                        setSize(s);
                      }}
                      aria-pressed={size === s}
                      className={`btn-y2k px-3 py-1.5 text-[10px] ${size === s ? "brightness-75" : ""}`}
                    >
                      US {s}
                    </button>
                  ))}
                </div>
                <table className="mt-4 w-full max-w-md border border-white/15 text-left text-[10px] text-white/70">
                  <thead className="border-b border-white/10 text-[9px] tracking-[0.3em] text-white/45">
                    <tr><th className="px-3 py-1.5">US</th><th className="px-3 py-1.5">EU</th><th className="px-3 py-1.5">INNER Ø</th></tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {[["6", "51.5", "16.5 mm"], ["7", "54.5", "17.3 mm"], ["8", "57", "18.2 mm"], ["9", "59.5", "19.0 mm"], ["10", "62", "19.8 mm"], ["11", "64.5", "20.6 mm"], ["12", "67", "21.4 mm"], ["13", "69.5", "22.2 mm"]].map(([us, eu, mm]) => (
                      <tr key={us}><td className="px-3 py-1">{us}</td><td className="px-3 py-1">{eu}</td><td className="px-3 py-1">{mm}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-8 flex items-center gap-4">
              <button
                data-testid={`acquire-button-detail-${product.id}`}
                onClick={acquire}
                className="btn-y2k flex items-center gap-2 px-6 py-3 text-xs"
              >
                <Zap size={13} /> ACQUIRE
              </button>
              <span className="text-[9px] tracking-[0.3em] text-white/45">SHIPS FROM THE LAST TRAIN DEPOT</span>
            </div>
          </motion.div>
        </div>
      </main>
      <CartDrawer />
    </div>
  );
}
